import {FC, ReactNode, useCallback, useEffect, useRef, useState} from 'react';
import Overlay from 'components/Layout/Overlay';
import {getReactRootDocument} from 'utils/dom';
import {parentTreeHasDataAttribute} from 'utils/functions';
import ModalBody from './ModalBody';
import ModalButton from './ModalButton';
import ModalContent from './ModalContent';
import ModalFooter from './ModalFooter';
import ModalHeader from './ModalHeader';

export type ModalProps = {
    children: ReactNode;
    className?: string;
    isOpen: boolean;
    onClose: () => void;
    preventOutsideClick?: boolean;
};

const DEFAULT_BUTTONS = {
    hasCancel: false,
    submitCount: 0,
};

type ModalComponentProps = FC<ModalProps> & {
    Body: typeof ModalBody;
    Button: typeof ModalButton;
    Footer: typeof ModalFooter;
    Header: typeof ModalHeader;
};

const Modal: ModalComponentProps = ({
    children,
    className,
    isOpen,
    onClose,
    preventOutsideClick,
}) => {
    const containerRef = useRef<HTMLDivElement | null>(null);

    const [{hasCancel, submitCount}, setButtons] = useState(
        () => DEFAULT_BUTTONS,
    );

    const onKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                if (hasCancel || submitCount === 0) {
                    onClose();
                }

                return;
            }

            if (
                event.key === 'Enter' && // If there is no submit, there must be a cancel so cancel
                submitCount === 0
            ) {
                onClose();
            }
        },
        [hasCancel, onClose, submitCount],
    );

    const onCheckButtons = useCallback(() => {
        const rootDocument = getReactRootDocument();
        const cancelButtons =
            rootDocument?.querySelectorAll(
                '[data-modal-button-type="cancel"]',
            ) || [];
        const submitButtons =
            rootDocument?.querySelectorAll(
                '[data-modal-button-type="submit"]',
            ) || [];

        if (cancelButtons.length > 0 || submitButtons.length > 0) {
            setButtons({
                hasCancel: cancelButtons.length > 0,
                submitCount: submitButtons.length,
            });
        }
    }, []);

    const onMouseDown = useCallback(
        (event: MouseEvent) => {
            const targetElement = event.composedPath()[0] as HTMLElement;

            if (!parentTreeHasDataAttribute(targetElement, 'modal-safe')) {
                onClose();
            }
        },
        [onClose],
    );

    useEffect(() => {
        if (isOpen) {
            // Prevent document.body from scrolling
            document.body.classList.add('overflow-hidden');

            if (!preventOutsideClick) {
                document.addEventListener('keydown', onKeyDown);
                document.addEventListener('mousedown', onMouseDown);
            }
        }

        return () => {
            document.body.classList.remove('overflow-hidden');
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('mousedown', onMouseDown);
        };
    }, [isOpen, onKeyDown, onMouseDown, preventOutsideClick]);

    if (!isOpen) {
        return null;
    }

    return (
        <Overlay
            className="modal-overlay"
            layoutLayer="z-modal"
            layoutMethod="modal"
        >
            <ModalContent
                ref={containerRef}
                className={className}
                onCheckButtons={onCheckButtons}
            >
                {children}
            </ModalContent>
        </Overlay>
    );
};

Modal.Body = ModalBody;
Modal.Button = ModalButton;
Modal.Footer = ModalFooter;
Modal.Header = ModalHeader;

export default Modal;
